import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Coins, Loader2, Send, Search, User, Store, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export default function SendDeuroModal({ recipient: initialRecipient, onClose, onDone }) {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const [recipient, setRecipient] = useState(initialRecipient || null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (recipient) return;
    const id = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/users/search", { params: { q: query } });
        setResults(data);
      } catch { /* ignore */ }
      setSearching(false);
    }, 250);
    return () => clearTimeout(id);
  }, [query, recipient]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) { setError(t("invalid_amount")); return; }
    if (amt > (user?.balance ?? 0)) { setError(t("insufficient")); return; }
    setLoading(true);
    try {
      await api.post("/wallet/transfer", { to_user_id: recipient.id, amount: amt, note });
      await refresh();
      setSuccess(true);
      onDone?.();
      setTimeout(onClose, 1200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
        onClick={onClose} data-testid="send-deuro-modal">
        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-white rounded-2xl p-6 card-shadow max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {recipient && !initialRecipient && (
                <button onClick={() => setRecipient(null)} data-testid="back-recipient" className="text-[#5f6b62] hover:text-[#1b241e]"><ArrowLeft className="w-5 h-5" /></button>
              )}
              <h3 className="font-head text-xl font-bold">{t("send_deuro_btn")}</h3>
            </div>
            <button onClick={onClose} data-testid="close-modal" className="text-[#5f6b62] hover:text-[#1b241e]"><X className="w-5 h-5" /></button>
          </div>

          {success ? (
            <div className="text-center py-8" data-testid="transfer-success">
              <div className="w-16 h-16 rounded-full bg-[#d1fae5] flex items-center justify-center mx-auto mb-3">
                <Send className="w-8 h-8 text-[#059669]" />
              </div>
              <p className="font-head font-bold text-lg">{parseInt(amount, 10).toLocaleString("it-IT")} D€ → {recipient.name}</p>
            </div>
          ) : !recipient ? (
            <div data-testid="recipient-picker">
              <p className="text-sm text-[#5f6b62] mb-3">{t("choose_recipient")}</p>
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-[#5f6b62] absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input data-testid="recipient-search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                  className="deuro-input pl-10" placeholder={t("search_user")} />
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {searching && <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#d97706]" /></div>}
                {!searching && results.map((r) => (
                  <button key={r.id} onClick={() => setRecipient(r)} data-testid={`recipient-${r.id}`}
                    className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#f4f0e8] transition-colors text-left">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-head font-bold ${r.user_type === "attivita" ? "bg-[#d97706]" : "bg-[#1e3326]"}`}>
                      {(r.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate flex items-center gap-1">
                        {r.user_type === "attivita" ? <Store className="w-3 h-3 text-[#d97706]" /> : <User className="w-3 h-3 text-[#1e3326]" />} {r.name}
                      </p>
                      <p className="text-xs text-[#5f6b62]">{r.username ? `@${r.username} · ` : ""}{r.city || "—"}</p>
                    </div>
                  </button>
                ))}
                {!searching && results.length === 0 && <p className="text-sm text-[#5f6b62] text-center py-6">—</p>}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-[#f4f0e8]">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-head font-bold ${recipient.user_type === "attivita" ? "bg-[#d97706]" : "bg-[#1e3326]"}`}>
                  {(recipient.name || "?").charAt(0).toUpperCase()}
                </div>
                <div><p className="font-semibold text-sm">{recipient.name}</p><p className="text-xs text-[#5f6b62]">{recipient.username ? `@${recipient.username} · ` : ""}{recipient.city || "—"}</p></div>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <span className="text-sm font-medium">{t("amount_label")}</span>
                  <div className="mt-1.5 relative">
                    <Coins className="w-4 h-4 text-[#d97706] absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input data-testid="transfer-amount" type="number" min="1" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)}
                      className="deuro-input pl-10 font-mono font-semibold" placeholder="500" />
                  </div>
                  <p className="text-xs text-[#5f6b62] mt-1">{t("balance_label")}: <span className="font-mono">{(user?.balance ?? 0).toLocaleString("it-IT")} D€</span></p>
                </div>
                <div>
                  <span className="text-sm font-medium">{t("note_label")}</span>
                  <input data-testid="transfer-note" value={note} onChange={(e) => setNote(e.target.value)}
                    className="deuro-input mt-1.5" placeholder={t("note_ph")} />
                </div>
                {error && <div data-testid="transfer-error" className="text-sm text-[#d9532f] bg-[#fdeae5] rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={loading} data-testid="transfer-deuro-submit"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#d97706] text-white font-semibold hover:bg-[#c26806] active:scale-[0.99] transition-all disabled:opacity-60">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> {t("send")} {amount ? `${parseInt(amount, 10).toLocaleString("it-IT")} D€` : ""}</>}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
